// Tile annotator: draw lesion outlines on 1000 px tiles, quadrant by quadrant (2x2 or 4x4), per annotator.
// Ported from the local patch viewer's Annotate tab.
//
// Only file NAMES and annotations live on the server. The photos stay on the annotator's computer:
// "Connect local files" matches the chosen files to the server's image list by file name (ignoring the
// extension), and the photo is shown from the local file only.
//
// mountTileAnnotator(root, { token, onAuthExpired, isStale }) builds the UI inside `root` and returns a
// cleanup function (removes the document/window listeners).

const TEMPLATE = `
<div class="ta-main">
  <div class="ta-top">
    <label class="ta-row" title="Annotator: shows the patients allocated to them">👤 <select id="ta-annotator"></select></label>
    <select id="ta-patient"></select>
    <button class="ta-btn" id="ta-previmg" title="previous image">◀</button>
    <select id="ta-image"></select>
    <button class="ta-btn" id="ta-nextimg" title="next image">▶</button>
    <div id="ta-quads"></div>
    <span id="ta-reviewing" hidden>🔁 Re-reviewing <button class="ta-collapse" id="ta-reviewstop" title="Stop re-reviewing">Stop</button></span>
    <span id="ta-status"></span>
    <button class="ta-collapse" id="ta-topcollapse"></button>
  </div>
  <div id="ta-banner" class="ta-banner" hidden></div>
  <div id="ta-stage">
    <div id="ta-world"><img alt="" draggable="false"><canvas width="1000" height="1000"></canvas><div id="ta-focus"></div><canvas id="ta-open" width="1000" height="1000"></canvas></div>
    <div id="ta-noimg" hidden></div>
    <div id="ta-cursor"></div>
    <div id="ta-hint"></div>
  </div>
  <div class="ta-zoombar">
    <button class="ta-btn" id="ta-zbout" title="Zoom out (−)">−</button>
    <input id="ta-zslider" type="range" min="0" max="100" step="1"
      title="Zoom: 50% fits the quadrant (or the whole image in the overview) to the screen">
    <button class="ta-btn" id="ta-zbin" title="Zoom in (+)">+</button>
    <span id="ta-zbv" class="ta-val"></span>
    <button class="ta-btn" id="ta-zbfit" title="Back to 50%: fit to the screen (F)">Fit</button>
    <button class="ta-btn" id="ta-zbmove" title="Move tool on / off (V)">✋ Move</button>
  </div>
</div>
<div class="ta-side">
  <button class="ta-collapse" id="ta-sidecollapse"></button>
  <div class="ta-group">
    <h3>Local files</h3>
    <div class="ta-row2">
      <button class="ta-btn ta-primary" id="ta-connect">📂 Connect local files</button>
      <button class="ta-btn" id="ta-connect-dir" title="Choose a whole folder">Folder…</button>
    </div>
    <input type="file" id="ta-files" accept="image/*" multiple hidden>
    <input type="file" id="ta-dir" webkitdirectory multiple hidden>
    <div class="ta-note" id="ta-local"></div>
  </div>
  <div class="ta-group">
    <h3>Tools</h3>
    <div class="ta-tools">
      <button data-tool="move">✋ Move <kbd>V</kbd></button>
      <button data-tool="pen">✏️ Pen <kbd>B</kbd></button>
      <button data-tool="eraser">🧽 Eraser <kbd>E</kbd></button>
    </div>
    <label><input id="ta-smart" type="checkbox"> <span><b>Smart annotate</b> <kbd>S</kbd>: trim start / end tails off circles</span></label>
    <label><input id="ta-smartjoin" type="checkbox"> <span>… and join the ends of a circle that stops just short</span></label>
  </div>
  <div class="ta-group">
    <div class="ta-row">Pen size <input id="ta-pen" type="range" min="1" max="40" step="1"><span id="ta-penv" class="ta-val"></span></div>
    <div class="ta-row">Eraser size <input id="ta-er" type="range" min="2" max="150" step="1"><span id="ta-erv" class="ta-val"></span></div>
    <div class="ta-note">Sizes are in image pixels. <kbd>[</kbd>/<kbd>]</kbd> change the active tool's size.</div>
  </div>
  <div class="ta-group">
    <h3>View</h3>
    <div class="ta-row2"><button class="ta-btn" id="ta-zout">− Zoom out</button><button class="ta-btn" id="ta-zin">+ Zoom in</button></div>
    <div class="ta-row2"><button class="ta-btn" id="ta-fit">Fit quadrant <kbd>F</kbd></button><button class="ta-btn" id="ta-1to1">1:1 pixels</button></div>
    <div class="ta-row">Quadrants <div class="ta-seg" id="ta-grid"><button data-g="2">2×2 (4)</button><button data-g="4">4×4 (16)</button></div></div>
    <div class="ta-row ta-spread">Zoom <b id="ta-zoomv"></b></div>
    <div class="ta-row">Outlines <input id="ta-color" type="color"> <input id="ta-op" type="range" min="0.1" max="1" step="0.05"></div>
    <label><input id="ta-show" type="checkbox" checked> Show outlines <kbd>H</kbd></label>
    <label><input id="ta-clip" type="checkbox" checked> Only draw inside the active quadrant</label>
    <label><input id="ta-pix" type="checkbox"> Pixelated photo when zoomed</label>
  </div>
  <div class="ta-group">
    <h3>Edit</h3>
    <div class="ta-row2"><button class="ta-btn" id="ta-undo">↶ Undo</button><button class="ta-btn" id="ta-redo">↷ Redo</button></div>
    <button class="ta-btn" id="ta-clearq">Clear this quadrant…</button>
    <div class="ta-row2">
      <button class="ta-btn" id="ta-hlopen" title="Show every stroke that doesn't enclose an area, in red (U)">⚠ Highlight unclosed</button>
      <button class="ta-btn" id="ta-delopen" title="Delete every stroke in this image that doesn't enclose an area">🗑 Delete unclosed…</button>
    </div>
    <div class="ta-note" id="ta-openinfo"></div>
  </div>
  <div class="ta-group">
    <h3>Progress</h3>
    <label><input id="ta-qdone" type="checkbox"> This quadrant is done <kbd>D</kbd></label>
    <button class="ta-btn ta-done" id="ta-donebtn">✓ Done → next quadrant <kbd>N</kbd></button>
    <button class="ta-btn" id="ta-review"
      title="Go through every quadrant of this image again (done ones included) to check and edit them, then on to the next finished image">🔁 Re-review this image</button>
    <div id="ta-stats" class="ta-note"></div>
    <textarea id="ta-note" placeholder="Note for this image"></textarea>
  </div>
  <div class="ta-keys">
    <h3>Keys</h3>
    <kbd>B</kbd> pen · <kbd>E</kbd> eraser · <kbd>V</kbd> move · hold <kbd>Space</kbd> to pan<br>
    wheel: zoom at cursor · <kbd>+</kbd>/<kbd>−</kbd> zoom · <kbd>F</kbd> fit quadrant<br>
    <kbd>1</kbd>–<kbd>4</kbd> quadrant (2×2) · <kbd>←</kbd><kbd>↑</kbd><kbd>→</kbd><kbd>↓</kbd> neighbouring quadrant<br>
    <kbd>O</kbd> overview · <kbd>N</kbd> done → next · <kbd>S</kbd> smart annotate on / off<br>
    <kbd>U</kbd> highlight unclosed circles<br>
    <kbd>Ctrl</kbd>+<kbd>Z</kbd> undo · <kbd>Ctrl</kbd>+<kbd>Y</kbd> redo · <kbd>H</kbd> hide outlines
  </div>
  <div class="ta-note">Photos never leave this computer: only file names, outlines, quadrant progress and notes are stored
    online. Saves happen automatically a moment after each change.</div>
</div>`;

const LS = (k) => { try { return JSON.parse(localStorage.getItem('ta:' + k)); } catch { return null; } };
const SS = (k, v) => { try { localStorage.setItem('ta:' + k, JSON.stringify(v)); } catch { /* private mode */ } };
const stripExtension = (n) => n.replace(/\.[^.]+$/, '');
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const T = 1000; // tile size (px)
const FULL = { x: 0, y: 0, w: T, h: T };

export function mountTileAnnotator(root, { token, onAuthExpired = () => {}, isStale = () => false }) {
    root.classList.add('ta-root');
    root.innerHTML = TEMPLATE;
    const $ = (id) => root.querySelector('#' + id);
    const cleanups = [];
    const on = (target, ev, fn, opts) => { target.addEventListener(ev, fn, opts); cleanups.push(() => target.removeEventListener(ev, fn, opts)); };
    const api = async (path, opts = {}) => {
        const r = await fetch(path, { ...opts, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...(opts.headers || {}) } });
        if (r.status === 401) onAuthExpired();
        return r;
    };

    const AN = {
        items: [], me: {}, who: null, annotators: [], cur: null, q: -1, tool: LS('tool') || 'pen', grid: LS('grid') === 4 ? 4 : 2,
        pen: LS('pen') || 2, er: LS('er') || 20, color: LS('color') || '#00e5ff', op: LS('op') ?? 1,
        view: { s: 1, tx: 0, ty: 0 }, base: 0, undo: [], redo: [], dirty: false, saving: null, saveTimer: 0,
        drawing: null, pan: null, space: false, loaded: false, loadFailed: false, alive: true,
        review: false, // re-reviewing: Done → next steps through every quadrant, then the next finished image
        hlOpen: false, hlTimer: 0, // highlighting the unclosed strokes (#ta-open)
        local: new Map(), // file name without extension -> File (the connected local photos)
        imgUrl: null,
        data: document.createElement('canvas') // the strokes (white, alpha = ink); the visible canvas is a recoloured copy
    };
    AN.data.width = AN.data.height = T;
    AN.lastDraw = AN.tool === 'eraser' ? 'eraser' : 'pen'; // the drawing tool the Move toggle returns to
    const anD = AN.data.getContext('2d', { willReadFrequently: true });
    const anCanvas = root.querySelector('#ta-world canvas'); const anC = anCanvas.getContext('2d');
    const anImg = root.querySelector('#ta-world img'); const anStage = $('ta-stage');

    // Quadrants: a 2x2 or 4x4 grid (AN.grid). Progress is kept per cell of the 4x4 grid (it.cells, row-major); a 2x2
    // quadrant is done when its four cells are, so switching grids never loses or invents progress.
    const QR = (q) => { const w = T / AN.grid; return { x: (q % AN.grid) * w, y: Math.floor(q / AN.grid) * w, w, h: w }; };
    const anNQ = () => AN.grid * AN.grid;
    const anCellsOf = (q) => (AN.grid === 4 ? [q] : [0, 1, 4, 5].map((o) => (q >> 1) * 8 + (q % 2) * 2 + o));
    const anIsDone = (it, q) => anCellsOf(q).every((c) => !!((it && it.cells) || [])[c]);
    const anQName = (q) => `Q${q + 1}`;
    const anParse = (it) => {
        const m = (it.file || it.name).match(/([^-_]+)-Seq(\d+)-.*TF\((\d+), (\d+)\)/) || [];
        return m.length ? `${m[1]} · Seq ${m[2]} · (${m[3]}, ${m[4]})` : it.name;
    };
    const anMine = () => AN.items.filter((i) => i.annotator === AN.who);
    const anQDone = (it) => [...Array(anNQ()).keys()].filter((q) => anIsDone(it, q)).length;
    const hasLocal = (it) => AN.local.has(stripExtension(it.name));
    // Drawing / marking needs: the photo shown, the saved outlines loaded, write access, a current build.
    const canEdit = () => AN.cur && AN.loaded && !AN.loadFailed && AN.cur.editable && hasLocal(AN.cur) && !isStale();
    const anStatus = (msg, cls) => { const s = $('ta-status'); s.textContent = msg; s.className = cls || ''; };

    // ---------------------------------------------------------------- setup
    root.querySelectorAll('.ta-tools button').forEach((b) => { b.onclick = () => anSetTool(b.dataset.tool); });
    $('ta-quads').onclick = (e) => { const b = e.target.closest('button'); if (b) anSetQuad(+b.dataset.q); };
    root.querySelectorAll('#ta-grid button').forEach((b) => { b.onclick = () => anSetGrid(+b.dataset.g); });
    $('ta-smart').checked = LS('smart') ?? true; $('ta-smartjoin').checked = LS('smartjoin') ?? true;
    $('ta-smart').onchange = () => SS('smart', $('ta-smart').checked);
    $('ta-smartjoin').onchange = () => SS('smartjoin', $('ta-smartjoin').checked);
    on(root, 'click', (e) => { const b = e.target.closest('button'); if (b) b.blur(); }); // keep Space for panning
    $('ta-pen').value = AN.pen; $('ta-er').value = AN.er; $('ta-color').value = AN.color; $('ta-op').value = AN.op;
    $('ta-pen').oninput = () => { AN.pen = +$('ta-pen').value; SS('pen', AN.pen); anSizeUI(); };
    $('ta-er').oninput = () => { AN.er = +$('ta-er').value; SS('er', AN.er); anSizeUI(); };
    $('ta-color').oninput = () => { AN.color = $('ta-color').value; SS('color', AN.color); anRedraw(); };
    $('ta-op').oninput = () => { AN.op = +$('ta-op').value; SS('op', AN.op); anRedraw(); };
    $('ta-show').onchange = anRedraw;
    $('ta-pix').checked = !!LS('pix');
    $('ta-pix').onchange = () => { SS('pix', $('ta-pix').checked); $('ta-world').classList.toggle('pix', $('ta-pix').checked); };
    $('ta-world').classList.toggle('pix', $('ta-pix').checked);
    $('ta-zin').onclick = () => anZoomCentre(1.5); $('ta-zout').onclick = () => anZoomCentre(1 / 1.5);
    $('ta-fit').onclick = () => anFit(AN.q >= 0 ? QR(AN.q) : FULL);
    $('ta-1to1').onclick = () => anZoomCentre(1 / AN.view.s);
    $('ta-undo').onclick = anUndo; $('ta-redo').onclick = anRedo;
    $('ta-clearq').onclick = anClearQuad;
    $('ta-hlopen').onclick = anToggleOpen; $('ta-delopen').onclick = anDeleteOpen;
    $('ta-qdone').onchange = () => anMarkQuad(AN.q, $('ta-qdone').checked);
    $('ta-donebtn').onclick = anDoneNext;
    $('ta-review').onclick = () => (AN.review ? anStopReview() : anStartReview());
    $('ta-reviewstop').onclick = anStopReview;
    $('ta-note').oninput = () => { if (AN.cur && AN.cur.editable) { AN.cur.note = $('ta-note').value; anMarkDirty(); } };
    $('ta-patient').onchange = () => { const it = anMine().find((i) => i.patient === $('ta-patient').value); if (it) anOpen(it.name); };
    $('ta-annotator').onchange = () => anSetAnnotator($('ta-annotator').value);
    $('ta-image').onchange = () => anOpen($('ta-image').value);
    $('ta-previmg').onclick = () => anStep(-1); $('ta-nextimg').onclick = () => anStep(1);
    $('ta-connect').onclick = () => $('ta-files').click();
    $('ta-connect-dir').onclick = () => $('ta-dir').click();
    $('ta-files').onchange = (e) => anConnect(e.target.files, e.target);
    $('ta-dir').onchange = (e) => anConnect(e.target.files, e.target);
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) $('ta-connect-dir').hidden = true; // no folder picker on iOS
    // Collapsible top bar and right sidebar (remembered on this computer). The stage's ResizeObserver keeps the view.
    const anPanels = () => {
        const top = !!LS('topCollapsed'); const side = !!LS('sideCollapsed');
        root.classList.toggle('top-collapsed', top); root.classList.toggle('side-collapsed', side);
        $('ta-topcollapse').textContent = top ? '▾' : '▴';
        $('ta-topcollapse').title = top ? 'Show the toolbar' : 'Hide the toolbar';
        $('ta-sidecollapse').textContent = side ? '◂' : '▸';
        $('ta-sidecollapse').title = side ? 'Show the sidebar' : 'Hide the sidebar';
    };
    $('ta-topcollapse').onclick = () => { SS('topCollapsed', !LS('topCollapsed')); anPanels(); };
    $('ta-sidecollapse').onclick = () => { SS('sideCollapsed', !LS('sideCollapsed')); anPanels(); };
    anPanels();
    $('ta-zslider').oninput = () => { if (AN.base) anZoomCentre(AN.base * zoomFactor(+$('ta-zslider').value) / AN.view.s); };
    $('ta-zbin').onclick = () => anZoomCentre(1.5); $('ta-zbout').onclick = () => anZoomCentre(1 / 1.5);
    $('ta-zbfit').onclick = () => anFit(anRegion());
    $('ta-zbmove').onclick = () => anSetTool(AN.tool === 'move' ? AN.lastDraw : 'move');
    anQuadButtons();
    anSizeUI();
    anLoadList();

    // ---------------------------------------------------------------- list / local files
    async function anLoadList() {
        let r;
        try {
            const res = await api('/api/tiles/list');
            if (!res.ok) throw new Error(`${res.status}`);
            r = await res.json();
        } catch (e) {
            anStatus(`Could not load the image list (${e.message}) - reload the page`, 'err');
            return;
        }
        if (!AN.alive) return;
        AN.me = r.me || {};
        AN.items = r.items.map((i) => (AN.cur && i.name === AN.cur.name ? AN.cur : i));
        AN.items.forEach((i) => { if (!Array.isArray(i.cells) || i.cells.length !== 16) i.cells = Array(16).fill(false); });
        AN.annotators = r.annotators || [...new Set(AN.items.map((i) => i.annotator))];
        const saved = LS('who');
        AN.who = AN.annotators.includes(saved) ? saved : AN.annotators.includes(AN.me.annotator) ? AN.me.annotator : AN.annotators[0];
        anAnnotatorUI();
        anBanner();
        anLocalUI();
        if (!AN.items.length) { anNone('No images have been added to the annotator yet.'); return; }
        const mine = anMine();
        if (!mine.length) { anNone(`No images allocated to ${AN.who} yet.`); return; }
        const want = LS('name:' + AN.who);
        await anOpen((mine.find((i) => i.name === want) || mine[0]).name);
    }
    function anAnnotatorUI() {
        $('ta-annotator').innerHTML = AN.annotators.map((n) => {
            const its = AN.items.filter((i) => i.annotator === n);
            return `<option value="${esc(n)}">${esc(n)}${n === AN.me.annotator ? ' (you)' : ''} · ${new Set(its.map((i) => i.patient)).size} patients · ${its.length} images</option>`;
        }).join('');
        $('ta-annotator').value = AN.who || '';
    }
    function anBanner() {
        const b = $('ta-banner');
        let msg = '';
        if (!AN.me.annotator && !AN.me.admin) {
            msg = `Your login (${AN.me.email || 'guest'}) isn't linked to an annotator yet, so you can look but not save. Ask Alex to add it.`;
        } else if (AN.cur && !AN.cur.editable) {
            msg = `View only: ${esc(AN.cur.patient)} is allocated to ${esc(AN.cur.annotator)}.`;
        }
        b.hidden = !msg; b.innerHTML = msg;
    }
    function anLocalUI() {
        const n = AN.items.filter(hasLocal).length, mine = anMine(), m = mine.filter(hasLocal).length;
        $('ta-local').innerHTML = !AN.local.size
            ? 'No local photos connected yet. Choose the image files (or their folder) on this computer - they are matched to the list by file name.'
            : `${AN.local.size} local file${AN.local.size === 1 ? '' : 's'} connected · <b>${m} of ${mine.length}</b> of ${esc(AN.who)}'s images found` +
              (n > m ? ` (${n} of ${AN.items.length} overall)` : '');
    }
    async function anConnect(fileList, input) {
        const files = [...fileList].filter((f) => f.type.startsWith('image/') || /\.(jpe?g|png|tiff?|bmp|webp)$/i.test(f.name));
        files.forEach((f) => AN.local.set(stripExtension(f.name), f));
        input.value = '';
        anLocalUI(); anPatientsUI();
        if (AN.cur) await anShowImage(AN.cur); // the open image may just have become available
    }

    async function anSetAnnotator(who) {
        await anFlush();
        if (AN.dirty) { alert('The current image could not be saved - not switching, so nothing is lost. Check the connection.'); $('ta-annotator').value = AN.who; return; }
        AN.who = who; SS('who', who);
        anLocalUI();
        const mine = anMine();
        if (!mine.length) { anNone(`No images allocated to ${who} yet.`); return; }
        if (AN.cur && AN.cur.annotator === who) { anPatientsUI(); return; }
        await anOpen((mine.find((i) => i.name === LS('name:' + who)) || mine[0]).name);
    }
    function anNone(msg) {
        AN.cur = null; $('ta-patient').innerHTML = ''; $('ta-image').innerHTML = ''; anImg.removeAttribute('src');
        anD.clearRect(0, 0, T, T); anRedraw(); anStatus(''); anNoImage(null);
        $('ta-hint').textContent = msg;
        anStrokesChanged();
    }
    function anPatientsUI() {
        const pats = [...new Set(anMine().map((i) => i.patient))];
        const v = $('ta-patient').value;
        $('ta-patient').innerHTML = pats.map((p) => {
            const its = anMine().filter((i) => i.patient === p); const done = its.reduce((a, i) => a + anQDone(i), 0);
            const loc = its.filter(hasLocal).length;
            return `<option value="${esc(p)}">${esc(p)} · ${its.length} image${its.length > 1 ? 's' : ''} · ${done}/${its.length * anNQ()} quadrants` +
                (AN.local.size && loc < its.length ? ` · ${its.length - loc} not on this computer` : '') + '</option>';
        }).join('');
        $('ta-patient').value = AN.cur && pats.includes(AN.cur.patient) ? AN.cur.patient : v || pats[0];
        if (AN.cur) anFillImages(AN.cur.patient);
    }
    function anFillImages(patient) {
        const its = anMine().filter((i) => i.patient === patient); // only this annotator's images of the patient
        const multi = new Set(its.map((i) => i.scan)).size > 1;
        $('ta-image').innerHTML = its.map((i) => {
            const d = (i.scan.split('_')[1] || '');
            return `<option value="${esc(i.name)}">${esc(anParse(i))}${multi && d ? ` · ${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}` : ''}` +
                ` · ${anQDone(i)}/${anNQ()} done${hasLocal(i) ? '' : ' · not on this computer'}</option>`;
        }).join('');
        if (AN.cur && AN.cur.patient === patient) $('ta-image').value = AN.cur.name;
    }

    async function anOpen(name) {
        await anFlush();
        if (AN.dirty) { alert('The current image could not be saved - not switching, so nothing is lost. Check the connection.'); return; }
        const it = AN.items.find((i) => i.name === name);
        if (!it) return;
        AN.cur = it; SS('name:' + it.annotator, name);
        AN.undo = []; AN.redo = []; AN.loaded = false; AN.loadFailed = false; anUndoUI();
        anPatientsUI(); $('ta-image').value = name;
        $('ta-note').value = it.note || ''; $('ta-note').disabled = !it.editable;
        anBanner();
        anD.clearRect(0, 0, T, T); anRedraw(); anStrokesChanged();
        anStatus('Loading…', '');
        anShowImage(it);
        if (it.has_strokes) {
            try {
                const res = await api(`/api/tiles/strokes/${encodeURIComponent(name)}`);
                if (!res.ok) throw new Error(`${res.status}`);
                const { png } = await res.json();
                const s = new Image(); s.src = png;
                await s.decode();
                if (AN.cur !== it) return;
                anD.drawImage(s, 0, 0, T, T);
            } catch (e) {
                if (AN.cur !== it) return;
                AN.loadFailed = true;
                anStatus(`Saved outlines failed to load (${e.message}) - editing disabled, reload the page`, 'err');
            }
        }
        if (AN.cur !== it) return;
        AN.loaded = true; anRedraw(); anStrokesChanged();
        anSetQuad(-1); anStats();
        if (!AN.loadFailed) {
            anStatus(it.saved ? `Saved ${new Date(it.saved).toLocaleString()}${it.savedBy ? ` by ${it.savedBy}` : ''}` : 'Not annotated yet', it.saved ? 'ok' : '');
        }
    }
    async function anShowImage(it) {
        if (AN.imgUrl) { URL.revokeObjectURL(AN.imgUrl); AN.imgUrl = null; }
        const f = AN.local.get(stripExtension(it.name));
        if (!f) { anImg.removeAttribute('src'); anNoImage(it); return; }
        anNoImage(null);
        AN.imgUrl = URL.createObjectURL(f);
        anImg.src = AN.imgUrl;
        anToolUI();
    }
    function anNoImage(it) {
        const d = $('ta-noimg');
        d.hidden = !it;
        if (it) {
            d.innerHTML = `<div><b>${esc(it.name)}</b> isn't connected on this computer.<br>` +
                `Use <b>📂 Connect local files</b> (right) and choose this photo, or the folder it is in.<br>` +
                `<span class="ta-note">Saved outlines are still shown; drawing needs the photo.</span></div>`;
        }
    }
    function anStep(d) {
        const mine = anMine(); const k = mine.indexOf(AN.cur); const n = mine[k + d];
        if (n) anOpen(n.name);
    }

    // ---------------------------------------------------------------- view
    // Zoom bar: 50% = the quadrant (or, in the overview, the whole image) fitted to the screen (AN.base).
    // The left half zooms out to 1/4 of that, the right half in to 8x.
    const Z_OUT = 4; const Z_IN = 8;
    const zoomFactor = (v) => (v <= 50 ? Math.pow(Z_OUT, (v - 50) / 50) : Math.pow(Z_IN, (v - 50) / 50));
    const zoomPos = (f) => Math.max(0, Math.min(100, 50 + 50 * Math.log(f) / Math.log(f <= 1 ? Z_OUT : Z_IN)));
    const anRegion = () => (AN.q >= 0 ? QR(AN.q) : FULL);
    function anApply() {
        const { s, tx, ty } = AN.view;
        $('ta-world').style.transform = `translate(${tx}px, ${ty}px) scale(${s})`;
        $('ta-zoomv').textContent = Math.round(s * 100) + '%' + (AN.q < 0 ? ' (overview)' : '');
        if (AN.base) {
            const pos = zoomPos(s / AN.base);
            $('ta-zslider').value = pos; $('ta-zbv').textContent = Math.round(pos) + '%';
        }
        anSizeUI();
    }
    function anFitScale(r) {
        const pad = 14;
        return Math.max(0.05, Math.min((anStage.clientWidth - 2 * pad) / r.w, (anStage.clientHeight - 2 * pad) / r.h));
    }
    function anFit(r) {
        const W = anStage.clientWidth; const H = anStage.clientHeight;
        const s = anFitScale(r); AN.base = s;
        AN.view = { s, tx: (W - r.w * s) / 2 - r.x * s, ty: (H - r.h * s) / 2 - r.y * s }; anApply();
    }
    function anZoomAt(f, cx, cy) {
        const v = AN.view; const ns = Math.min(48, Math.max(0.1, v.s * f)); const k = ns / v.s;
        v.tx = cx - (cx - v.tx) * k; v.ty = cy - (cy - v.ty) * k; v.s = ns; anApply();
    }
    function anZoomCentre(f) { anZoomAt(f, anStage.clientWidth / 2, anStage.clientHeight / 2); }
    function anToImg(e) {
        const r = anStage.getBoundingClientRect(); const v = AN.view;
        return { x: (e.clientX - r.left - v.tx) / v.s, y: (e.clientY - r.top - v.ty) / v.s };
    }
    function anRedraw() {
        if (anRedraw.raf) return;
        anRedraw.raf = requestAnimationFrame(() => {
            anRedraw.raf = 0;
            anC.globalCompositeOperation = 'copy'; anC.drawImage(AN.data, 0, 0);
            anC.globalCompositeOperation = 'source-in'; anC.fillStyle = AN.color; anC.fillRect(0, 0, T, T);
            anC.globalCompositeOperation = 'source-over';
            anCanvas.style.opacity = $('ta-show').checked ? AN.op : 0;
        });
    }

    function anQuadButtons() {
        const n = anNQ();
        $('ta-quads').classList.toggle('g4', AN.grid === 4);
        $('ta-world').classList.toggle('g4', AN.grid === 4);
        $('ta-quads').innerHTML = '<button class="ta-segbtn" data-q="-1" title="whole image (O)">Overview</button>' +
            [...Array(n).keys()].map((k) => `<button class="ta-segbtn" data-q="${k}">${anQName(k)}</button>`).join('');
        root.querySelectorAll('#ta-grid button').forEach((b) => b.classList.toggle('on', +b.dataset.g === AN.grid));
    }
    function anSetGrid(g) {
        if (g === AN.grid) return;
        if (AN.drawing) anEndStroke();
        AN.grid = g; SS('grid', g);
        anQuadButtons(); anPatientsUI(); anSetQuad(-1);
    }
    function anSetQuad(q) {
        if (AN.drawing) anEndStroke();
        AN.q = q;
        root.querySelectorAll('#ta-quads button').forEach((b) => {
            const k = +b.dataset.q; b.classList.toggle('on', k === q); b.classList.toggle('done', k >= 0 && anIsDone(AN.cur, k));
        });
        root.querySelectorAll('#ta-world .ta-q').forEach((e) => e.remove());
        const f = $('ta-focus');
        if (q < 0) {
            f.style.display = 'none';
            for (let k = 0; k < anNQ(); k++) {
                const r = QR(k); const d = document.createElement('div'); const done = anIsDone(AN.cur, k);
                d.className = 'ta-q' + (done ? ' done' : '');
                Object.assign(d.style, { left: r.x + 'px', top: r.y + 'px', width: r.w + 'px', height: r.h + 'px' });
                d.innerHTML = `<span>${anQName(k)}${done ? ' ✓' : ''}</span>`;
                $('ta-world').appendChild(d);
            }
            anFit(FULL);
        } else {
            const r = QR(q);
            Object.assign(f.style, { display: '', left: r.x + 'px', top: r.y + 'px', width: r.w + 'px', height: r.h + 'px' });
            anFit(r);
        }
        $('ta-qdone').checked = q >= 0 && anIsDone(AN.cur, q);
        const ro = !(AN.cur && AN.cur.editable);
        ['ta-qdone', 'ta-donebtn', 'ta-clearq'].forEach((id) => { $(id).disabled = q < 0 || ro; });
        anReviewUI();
        anToolUI();
    }

    // ---------------------------------------------------------------- tools
    function anSetTool(t) { AN.tool = t; if (t !== 'move') AN.lastDraw = t; SS('tool', t); anToolUI(); }
    function anToolUI() {
        root.querySelectorAll('.ta-tools button').forEach((b) => b.classList.toggle('on', b.dataset.tool === AN.tool));
        $('ta-zbmove').classList.toggle('on', AN.tool === 'move');
        $('ta-zbmove').textContent = AN.tool === 'move' ? '✋ Move: on' : '✋ Move: off';
        anStage.classList.remove('tool-move', 'tool-pen', 'tool-eraser', 'panning');
        if (AN.q >= 0 && canEdit()) anStage.classList.add(AN.space ? 'panning' : 'tool-' + AN.tool);
        else anStage.classList.add('panning');
        anSizeUI();
    }
    function anSizeUI() {
        $('ta-penv').textContent = AN.pen + ' px'; $('ta-erv').textContent = AN.er + ' px';
        const c = $('ta-cursor'); const size = (AN.tool === 'eraser' ? AN.er : AN.pen) * AN.view.s;
        c.style.width = c.style.height = Math.max(5, size) + 'px';
        c.style.borderStyle = AN.tool === 'eraser' ? 'dashed' : 'solid';
        if (anFlash.t) return;
        $('ta-hint').textContent = !AN.cur ? $('ta-hint').textContent
            : AN.q < 0 ? 'Click a quadrant to annotate it · drag to pan · wheel to zoom'
                : `${anQName(AN.q)} of ${anNQ()} · ${!canEdit() ? 'view only' : AN.tool === 'move' ? 'move' : AN.tool + ' ' + (AN.tool === 'eraser' ? AN.er : AN.pen) + ' px'}` +
                  ' · Space+drag or right-drag to pan · wheel to zoom';
    }
    function anCursorAt(e) {
        const c = $('ta-cursor'); const show = AN.q >= 0 && AN.tool !== 'move' && !AN.space && !AN.pan && canEdit();
        c.style.display = show ? 'block' : 'none';
        if (!show) return;
        const r = anStage.getBoundingClientRect();
        c.style.left = e.clientX - r.left + 'px'; c.style.top = e.clientY - r.top + 'px';
    }

    // ---------------------------------------------------------------- drawing
    // Every drawing step sets up (and fully undoes) its own clip + tool state, so a stroke that never
    // gets its pointerup can't leave a quadrant clip stuck on the context and block other quadrants.
    function anPaint(fn) {
        const d = AN.drawing;
        anD.save();
        anD.beginPath(); anD.rect(d.clip.x, d.clip.y, d.clip.w, d.clip.h); anD.clip();
        anD.globalCompositeOperation = d.erase ? 'destination-out' : 'source-over';
        anD.strokeStyle = anD.fillStyle = '#fff'; anD.lineCap = anD.lineJoin = 'round'; anD.lineWidth = d.width;
        anD.beginPath(); fn();
        anD.restore();
    }
    function anBeginStroke(p, pointerId) {
        if (AN.drawing) anEndStroke();
        const clip = $('ta-clip').checked ? QR(AN.q) : FULL;
        AN.undo.push({ ...clip, img: anD.getImageData(clip.x, clip.y, clip.w, clip.h) });
        if (AN.undo.length > 80) AN.undo.shift();
        AN.redo = [];
        AN.drawing = { last: p, pts: [p], clip, pointerId, erase: AN.tool === 'eraser', width: AN.tool === 'eraser' ? AN.er : AN.pen };
        anPaint(() => { anD.arc(p.x, p.y, AN.drawing.width / 2, 0, Math.PI * 2); anD.fill(); });
        anRedraw();
    }
    function anStrokeTo(p) {
        const l = AN.drawing.last;
        anPaint(() => { anD.moveTo(l.x, l.y); anD.lineTo(p.x, p.y); anD.stroke(); });
        AN.drawing.last = p; AN.drawing.pts.push(p);
    }
    function anEndStroke() {
        const d = AN.drawing;
        if (d && !d.erase && $('ta-smart').checked) anSmart(d);
        AN.drawing = null; anRedraw(); anMarkDirty(); anUndoUI(); anStrokesChanged();
    }

    // Smart annotate: a pen stroke that crosses itself keeps only the loop it closes (the largest one), so the
    // tails before the first and after the last crossing go; a near-closed circle can have its ends joined instead.
    // The stroke is redrawn from the snapshot taken when it started, so one Undo brings back the stroke as drawn.
    function anSegX(a, b, c, d) {
        const rx = b.x - a.x; const ry = b.y - a.y; const sx = d.x - c.x; const sy = d.y - c.y; const den = rx * sy - ry * sx;
        if (Math.abs(den) < 1e-9) return null;
        const qx = c.x - a.x; const qy = c.y - a.y; const t = (qx * sy - qy * sx) / den; const u = (qx * ry - qy * rx) / den;
        return t > 0 && t < 1 && u > 0 && u < 1 ? { x: a.x + t * rx, y: a.y + t * ry } : null;
    }
    function anArea(P) {
        let a = 0;
        for (let i = 0; i < P.length; i++) { const p = P[i]; const q = P[(i + 1) % P.length]; a += p.x * q.y - q.x * p.y; }
        return Math.abs(a) / 2;
    }
    function anSmartPath(pts, width, join) {
        const P = [];
        for (const p of pts) { const l = P[P.length - 1]; if (!l || Math.hypot(p.x - l.x, p.y - l.y) >= 0.75) P.push(p); }
        const n = P.length; const minArea = Math.max(60, 9 * width * width);
        if (n < 8) return null;
        let best = null;
        for (let i = 0; i < n - 3; i++) {
            const a = P[i]; const b = P[i + 1];
            const x0 = Math.min(a.x, b.x); const x1 = Math.max(a.x, b.x); const y0 = Math.min(a.y, b.y); const y1 = Math.max(a.y, b.y);
            for (let j = i + 2; j < n - 1; j++) {
                const c = P[j]; const d = P[j + 1];
                if (Math.max(c.x, d.x) < x0 || Math.min(c.x, d.x) > x1 || Math.max(c.y, d.y) < y0 || Math.min(c.y, d.y) > y1) continue;
                const X = anSegX(a, b, c, d);
                if (!X) continue;
                const loop = [X, ...P.slice(i + 1, j + 1)]; const A = anArea(loop);
                if (!best || A > best.A) best = { A, loop, cut: i + 1 + (n - 2 - j) };
            }
        }
        if (best && best.A >= minArea) return { pts: best.loop, msg: best.cut ? 'trimmed the tails off this circle' : null };
        if (join) {
            let len = 0;
            for (let i = 1; i < n; i++) len += Math.hypot(P[i].x - P[i - 1].x, P[i].y - P[i - 1].y);
            const gap = Math.hypot(P[n - 1].x - P[0].x, P[n - 1].y - P[0].y);
            if (gap > 0.5 && gap <= Math.max(12, 4 * width) && len >= 60 && len > 6 * gap && anArea(P) >= minArea) {
                return { pts: P, msg: `joined the ends of this circle (${gap.toFixed(0)} px gap)` };
            }
        }
        return null;
    }
    function anSmart(d) {
        const res = anSmartPath(d.pts, d.width, $('ta-smartjoin').checked);
        if (!res || !res.msg) return;
        const u = AN.undo[AN.undo.length - 1];
        anD.putImageData(u.img, u.x, u.y); // the canvas as it was before this stroke
        anPaint(() => { anD.moveTo(res.pts[0].x, res.pts[0].y); for (const q of res.pts.slice(1)) anD.lineTo(q.x, q.y); anD.closePath(); anD.stroke(); });
        anFlash(`Smart annotate: ${res.msg} (Ctrl+Z to undo)`);
    }
    function anFlash(msg) {
        const h = $('ta-hint');
        h.textContent = msg; h.classList.add('flash');
        clearTimeout(anFlash.t);
        anFlash.t = setTimeout(() => { anFlash.t = 0; h.classList.remove('flash'); anSizeUI(); }, 2500);
    }
    function anUndoUI() { $('ta-undo').disabled = !AN.undo.length; $('ta-redo').disabled = !AN.redo.length; }
    function anSwap(from, to) {
        const u = from.pop(); if (!u) return;
        to.push({ ...u, img: anD.getImageData(u.x, u.y, u.w, u.h) });
        anD.putImageData(u.img, u.x, u.y); anRedraw(); anMarkDirty(); anUndoUI(); anStrokesChanged();
    }
    function anUndo() { if (!AN.drawing && canEdit()) anSwap(AN.undo, AN.redo); }
    function anRedo() { if (!AN.drawing && canEdit()) anSwap(AN.redo, AN.undo); }
    function anClearQuad() {
        if (AN.q < 0 || !canEdit() || !window.confirm(`Erase every outline in ${anQName(AN.q)}? (Undo can bring them back)`)) return;
        const r = QR(AN.q);
        AN.undo.push({ ...r, img: anD.getImageData(r.x, r.y, r.w, r.h) }); AN.redo = [];
        anD.clearRect(r.x, r.y, r.w, r.h); anRedraw(); anMarkDirty(); anUndoUI(); anStrokesChanged();
    }

    // Unclosed circles: stroke components that enclose no area (the "open strokes" of anAnalyse). Highlighting draws
    // them in red on #ta-open with a box round each; it is recomputed shortly after every change to the outlines.
    function anToggleOpen() {
        AN.hlOpen = !AN.hlOpen;
        $('ta-hlopen').classList.toggle('on', AN.hlOpen);
        anHighlightOpen();
        if (AN.hlOpen && AN.cur && AN.loaded) {
            const n = anHighlightOpen.n;
            anFlash(n ? `${n} unclosed circle${n === 1 ? '' : 's'} highlighted in red` : 'No unclosed circles in this image');
        }
    }
    function anStrokesChanged() {
        if (!AN.hlOpen) return;
        clearTimeout(AN.hlTimer); AN.hlTimer = setTimeout(anHighlightOpen, 150);
    }
    function anHighlightOpen() {
        clearTimeout(AN.hlTimer);
        const c = $('ta-open'); const cx = c.getContext('2d');
        cx.clearRect(0, 0, T, T); anHighlightOpen.n = 0;
        if (!AN.hlOpen || !AN.cur || !AN.loaded) { $('ta-openinfo').textContent = ''; return; }
        const r = anAnalyse();
        const out = cx.createImageData(T, T); const box = new Map(); // component id -> bounding box
        for (let i = 0; i < T * T; i++) {
            const id = r.lab[i];
            if (!id || r.closed[id]) continue;
            out.data[i * 4] = 255; out.data[i * 4 + 1] = 59; out.data[i * 4 + 2] = 48; out.data[i * 4 + 3] = 255;
            const x = i % T; const y = (i - x) / T; const b = box.get(id);
            if (!b) box.set(id, { x0: x, y0: y, x1: x, y1: y });
            else { if (x < b.x0) b.x0 = x; if (x > b.x1) b.x1 = x; if (y > b.y1) b.y1 = y; }
        }
        cx.putImageData(out, 0, 0);
        cx.strokeStyle = '#ff3b30'; cx.lineWidth = 2; cx.setLineDash([6, 4]);
        for (const b of box.values()) cx.strokeRect(b.x0 - 6, b.y0 - 6, b.x1 - b.x0 + 12, b.y1 - b.y0 + 12);
        anHighlightOpen.n = r.nOpen;
        $('ta-openinfo').innerHTML = r.nOpen
            ? `<b class="ta-warn">${r.nOpen}</b> unclosed circle${r.nOpen === 1 ? '' : 's'} (red) - close or delete ${r.nOpen === 1 ? 'it' : 'them'}`
            : 'No unclosed circles in this image ✓';
    }
    function anDeleteOpen() {
        if (!canEdit() || AN.drawing) return;
        const r = anAnalyse();
        if (!r.nOpen) { anFlash('No unclosed circles to delete'); return; }
        if (!window.confirm(`Delete ${r.nOpen} unclosed circle${r.nOpen === 1 ? '' : 's'} (strokes that don't enclose an area) from the whole image? (Undo can bring them back)`)) return;
        AN.undo.push({ ...FULL, img: anD.getImageData(0, 0, T, T) });
        if (AN.undo.length > 80) AN.undo.shift();
        AN.redo = [];
        const d = r.img.data;
        for (let i = 0; i < T * T; i++) { const id = r.lab[i]; if (id && !r.closed[id]) d[i * 4] = d[i * 4 + 1] = d[i * 4 + 2] = d[i * 4 + 3] = 0; }
        anD.putImageData(r.img, 0, 0);
        anRedraw(); anMarkDirty(); anUndoUI(); anStrokesChanged();
        anFlash(`Deleted ${r.nOpen} unclosed circle${r.nOpen === 1 ? '' : 's'} (Ctrl+Z to undo)`);
    }

    on(anStage, 'pointerdown', (e) => {
        if (!AN.cur || !AN.loaded || e.button > 2) return;
        e.preventDefault();
        if (AN.drawing && AN.drawing.pointerId !== e.pointerId) return; // ignore a 2nd finger / pen while drawing
        try { anStage.setPointerCapture(e.pointerId); } catch { /* not capturable */ }
        const p = anToImg(e);
        if (AN.q < 0 || e.button !== 0 || AN.space || AN.tool === 'move' || !canEdit()) {
            AN.pan = { x: e.clientX, y: e.clientY, tx: AN.view.tx, ty: AN.view.ty, moved: false, p, button: e.button };
            anStage.classList.add('dragging'); anCursorAt(e);
            return;
        }
        anBeginStroke(p, e.pointerId);
    });
    on(anStage, 'pointermove', (e) => {
        anCursorAt(e);
        if (AN.pan) {
            const dx = e.clientX - AN.pan.x; const dy = e.clientY - AN.pan.y;
            if (Math.abs(dx) + Math.abs(dy) > 3) AN.pan.moved = true;
            AN.view.tx = AN.pan.tx + dx; AN.view.ty = AN.pan.ty + dy; anApply();
        } else if (AN.drawing && e.pointerId === AN.drawing.pointerId) {
            const evs = e.getCoalescedEvents ? e.getCoalescedEvents() : [];
            for (const ev of evs.length ? evs : [e]) anStrokeTo(anToImg(ev));
            anRedraw();
        }
    });
    function anPointerEnd() {
        if (AN.pan) {
            const pan = AN.pan; AN.pan = null; anStage.classList.remove('dragging');
            if (!pan.moved && AN.q < 0 && pan.button === 0) {
                const { x, y } = pan.p; const w = T / AN.grid;
                if (x >= 0 && y >= 0 && x < T && y < T) anSetQuad(Math.floor(x / w) + AN.grid * Math.floor(y / w));
            }
        } else if (AN.drawing) anEndStroke();
    }
    on(anStage, 'pointerup', anPointerEnd);
    on(anStage, 'pointercancel', anPointerEnd);
    on(anStage, 'lostpointercapture', anPointerEnd); // e.g. window lost focus mid-stroke
    on(window, 'blur', () => { if (AN.drawing || AN.pan) anPointerEnd(); AN.space = false; });
    on(anStage, 'pointerleave', () => { if (!AN.drawing && !AN.pan) $('ta-cursor').style.display = 'none'; });
    on(anStage, 'contextmenu', (e) => e.preventDefault());
    on(anStage, 'wheel', (e) => {
        e.preventDefault();
        const r = anStage.getBoundingClientRect();
        anZoomAt(Math.pow(1.0015, -e.deltaY), e.clientX - r.left, e.clientY - r.top);
    }, { passive: false });
    let stageSize = null;
    const ro = new ResizeObserver(() => {
        const W = anStage.clientWidth; const H = anStage.clientHeight;
        if (!W || !H) return;
        const prev = stageSize; stageSize = { W, H };
        if (!prev || !AN.base) { anFit(anRegion()); return; }
        if (prev.W === W && prev.H === H) return;
        const v = AN.view; const f = v.s / AN.base;
        const cx = (prev.W / 2 - v.tx) / v.s; const cy = (prev.H / 2 - v.ty) / v.s; // image point at the old centre
        AN.base = anFitScale(anRegion());
        v.s = Math.min(48, Math.max(0.1, AN.base * f));
        v.tx = W / 2 - cx * v.s; v.ty = H / 2 - cy * v.s; anApply();
    });
    ro.observe(anStage);
    cleanups.push(() => ro.disconnect());

    // ---------------------------------------------------------------- progress + saving
    function anMarkQuad(q, done) {
        if (!AN.cur || q < 0 || !AN.cur.editable || isStale()) { anSetQuadBadges(); return; }
        anCellsOf(q).forEach((c) => { AN.cur.cells[c] = done; });
        anSetQuadBadges(); anMarkDirty(); anPatientsUI();
    }
    function anSetQuadBadges() {
        root.querySelectorAll('#ta-quads button').forEach((b) => { const k = +b.dataset.q; b.classList.toggle('done', k >= 0 && anIsDone(AN.cur, k)); });
        $('ta-qdone').checked = AN.q >= 0 && anIsDone(AN.cur, AN.q);
        anReviewUI();
    }
    function anDoneNext() {
        if (AN.review) { anReviewNext(); return; }
        if (AN.q < 0 || !AN.cur || !AN.cur.editable) return;
        anMarkQuad(AN.q, true);
        const all = [...Array(anNQ()).keys()];
        const next = all.find((k) => k > AN.q && !anIsDone(AN.cur, k)) ?? all.find((k) => !anIsDone(AN.cur, k));
        if (next !== undefined) anSetQuad(next);
        else {
            const mine = anMine(); const k = mine.indexOf(AN.cur); const nxt = mine.slice(k + 1).find((i) => anQDone(i) < anNQ());
            if (nxt) anOpen(nxt.name); else { anSetQuad(-1); anStatus(`All ${anNQ()} quadrants done ✓`, 'ok'); }
        }
    }

    // Re-review: go back over an image whose quadrants are already done, quadrant by quadrant, to check and edit them.
    // "Checked → next" walks every quadrant in order (done ones included) and then opens the next finished image.
    function anReviewUI() {
        const it = AN.cur;
        root.classList.toggle('reviewing', AN.review);
        $('ta-reviewing').hidden = !AN.review;
        $('ta-review').textContent = AN.review ? '■ Stop re-reviewing' : '🔁 Re-review this image';
        $('ta-review').disabled = !AN.review && !(it && AN.loaded && anQDone(it) > 0);
        $('ta-donebtn').innerHTML = AN.review ? '✓ Checked → next quadrant <kbd>N</kbd>' : '✓ Done → next quadrant <kbd>N</kbd>';
        $('ta-donebtn').disabled = AN.q < 0 || (!AN.review && !(it && it.editable)); // view-only images can be re-reviewed
    }
    function anStartReview() {
        if (!AN.cur || !AN.loaded) return;
        AN.review = true;
        anSetQuad(0);
        anFlash(`Re-reviewing: check and fix ${anQName(0)}, then ✓ Checked → next (N)`);
    }
    function anStopReview() {
        AN.review = false;
        anReviewUI();
        anFlash('Stopped re-reviewing');
    }
    async function anReviewNext() {
        if (AN.q < 0 || !AN.cur) return;
        if (AN.cur.editable && !anIsDone(AN.cur, AN.q)) anMarkQuad(AN.q, true);
        if (AN.q < anNQ() - 1) { anSetQuad(AN.q + 1); return; }
        const mine = anMine(); const k = mine.indexOf(AN.cur);
        const nxt = mine.slice(k + 1).find((i) => anQDone(i) === anNQ());
        if (!nxt) {
            AN.review = false; anSetQuad(-1);
            anFlash(`Re-review finished: no more finished images of ${AN.who} after this one`);
            return;
        }
        await anOpen(nxt.name);
        if (AN.cur === nxt && AN.review) { anSetQuad(0); anFlash(`Re-reviewing ${anParse(nxt)}: ${anQName(0)}`); }
    }
    function anNeighbour(dx, dy) {
        if (AN.q < 0) return;
        const G = AN.grid; const c = (AN.q % G) + dx; const r = Math.floor(AN.q / G) + dy;
        if (c >= 0 && c < G && r >= 0 && r < G) anSetQuad(r * G + c);
    }
    function anStats() {
        const it = AN.cur;
        $('ta-stats').innerHTML = !it ? '' : it.n_closed == null ? 'No outlines saved yet.'
            : `Last save: <b>${it.n_closed}</b> closed outline${it.n_closed === 1 ? '' : 's'} (filled in the mask)` +
              (it.n_open ? ` · <b class="ta-warn">${it.n_open}</b> open stroke${it.n_open === 1 ? '' : 's'} - not filled; close the circle to count it` : '');
    }
    function anMarkDirty() {
        if (!AN.cur || !AN.cur.editable) return;
        AN.dirty = true; anStatus('Unsaved changes…', 'dirty');
        clearTimeout(AN.saveTimer); AN.saveTimer = setTimeout(anSave, 1200);
    }

    // The mask = the strokes + the inside of every closed outline: background pixels that can't be reached from the
    // image border (4-connected) are holes, as scipy's binary_fill_holes. Components (4-connected) with any filled
    // interior are closed outlines, the rest open strokes - the same counts the local patch viewer reports.
    // anAnalyse returns the stroke canvas's pixels (img), bg (1 = outside every outline), lab (component id per
    // non-background pixel, 0 = background) and closed[id] (1 = the component encloses an area).
    function anAnalyse() {
        const img = anD.getImageData(0, 0, T, T); const a = img.data; const N = T * T;
        const s = new Uint8Array(N); let strokePx = 0;
        for (let i = 0; i < N; i++) if (a[i * 4 + 3] > 0) { s[i] = 1; strokePx++; }
        const bg = new Uint8Array(N); const st = new Int32Array(N); let n = 0;
        const push = (i) => { if (!s[i] && !bg[i]) { bg[i] = 1; st[n++] = i; } };
        for (let x = 0; x < T; x++) { push(x); push((T - 1) * T + x); }
        for (let y = 0; y < T; y++) { push(y * T); push(y * T + T - 1); }
        while (n) {
            const i = st[--n]; const x = i % T;
            if (x > 0) push(i - 1);
            if (x < T - 1) push(i + 1);
            if (i >= T) push(i - T);
            if (i < N - T) push(i + T);
        }
        const lab = new Int32Array(N); const closed = [0]; let nClosed = 0; let nOpen = 0; let maskPx = 0;
        for (let i0 = 0; i0 < N; i0++) {
            if (bg[i0] || lab[i0]) continue;
            const id = closed.length; let interior = false; n = 0; lab[i0] = id; st[n++] = i0;
            while (n) {
                const i = st[--n]; const x = i % T; maskPx++;
                if (!s[i]) interior = true;
                const nb = [x > 0 ? i - 1 : -1, x < T - 1 ? i + 1 : -1, i >= T ? i - T : -1, i < N - T ? i + T : -1];
                for (const j of nb) if (j >= 0 && !bg[j] && !lab[j]) { lab[j] = id; st[n++] = j; }
            }
            closed.push(interior ? 1 : 0);
            if (interior) nClosed++; else nOpen++;
        }
        return { img, bg, lab, closed, nClosed, nOpen, strokePx, maskPx };
    }
    function anMaskAndStats() {
        const { bg, nClosed, nOpen, strokePx, maskPx } = anAnalyse(); const N = T * T;
        const c = document.createElement('canvas'); c.width = c.height = T;
        const cx = c.getContext('2d'); const out = cx.createImageData(T, T);
        for (let i = 0; i < N; i++) { const v = bg[i] ? 0 : 255; out.data[i * 4] = out.data[i * 4 + 1] = out.data[i * 4 + 2] = v; out.data[i * 4 + 3] = 255; }
        cx.putImageData(out, 0, 0);
        return { maskPng: c.toDataURL('image/png'), nClosed, nOpen, strokePx, maskPx };
    }
    async function anSave() {
        clearTimeout(AN.saveTimer);
        if (AN.saving) await AN.saving;
        const it = AN.cur;
        if (!it || !AN.dirty) return;
        if (AN.loadFailed) { anStatus('Not saved: the existing outlines failed to load - reload the page', 'err'); return; }
        if (isStale()) { anStatus('Not saved: this page is out of date - reload it (see the red banner)', 'err'); return; }
        if (AN.drawing) { AN.saveTimer = setTimeout(anSave, 600); return; }
        AN.dirty = false; anStatus('Saving…', '');
        const m = anMaskAndStats();
        const body = JSON.stringify({ name: it.name, strokesPng: AN.data.toDataURL('image/png'), maskPng: m.maskPng,
            cells: it.cells, note: it.note || '', nClosed: m.nClosed, nOpen: m.nOpen, strokePx: m.strokePx, maskPx: m.maskPx });
        AN.saving = api('/api/tiles/save', { method: 'POST', body })
            .then(async (r) => {
                if (!r.ok) {
                    const err = await r.json().catch(() => ({}));
                    const e = new Error(err.error || `${r.status} ${r.statusText}`); e.status = r.status; throw e;
                }
                const res = await r.json();
                Object.assign(it, { saved: res.saved, savedBy: res.savedBy, n_closed: res.n_closed, n_open: res.n_open, has_strokes: true });
                if (AN.cur === it) { if (!AN.dirty) anStatus(`Saved ${new Date(res.saved).toLocaleTimeString()}`, 'ok'); anStats(); }
                anPatientsUI();
            })
            .catch((err) => {
                AN.dirty = true;
                if (err.status === 401 || err.status === 403) { anStatus(`Not saved: ${err.message}`, 'err'); return; }
                anStatus(`Save failed (${err.message}) - retrying`, 'err');
                AN.saveTimer = setTimeout(anSave, 4000);
            })
            .finally(() => { AN.saving = null; });
        await AN.saving;
    }
    async function anFlush() {
        if (AN.drawing) anEndStroke();
        if (AN.dirty) await anSave(); else if (AN.saving) await AN.saving;
    }
    on(window, 'beforeunload', (e) => {
        if (!AN.dirty && !AN.saving) return;
        anSave();
        e.preventDefault(); e.returnValue = ''; // ask the browser to wait: the save is on its way
    });

    on(document, 'keydown', (e) => {
        if (e.target.matches('input[type=text], textarea, select')) return;
        const k = e.key;
        if ((e.ctrlKey || e.metaKey) && (k === 'z' || k === 'Z')) { e.preventDefault(); if (e.shiftKey) anRedo(); else anUndo(); return; }
        if ((e.ctrlKey || e.metaKey) && (k === 'y' || k === 'Y')) { e.preventDefault(); anRedo(); return; }
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        if (k === ' ') { e.preventDefault(); if (!AN.space) { AN.space = true; anToolUI(); $('ta-cursor').style.display = 'none'; } return; }
        const cur = AN.tool === 'eraser' ? 'er' : 'pen';
        switch (k.toLowerCase()) {
        case 'b': case 'p': anSetTool('pen'); break;
        case 'e': anSetTool('eraser'); break;
        case 'v': case 'm': anSetTool('move'); break;
        case 'f': anFit(AN.q >= 0 ? QR(AN.q) : FULL); break;
        case 'o': anSetQuad(-1); break;
        case '1': case '2': case '3': case '4': if (AN.grid === 2) anSetQuad(+k - 1); break;
        case 'arrowleft': anNeighbour(-1, 0); break;
        case 'arrowright': anNeighbour(1, 0); break;
        case 'arrowup': anNeighbour(0, -1); break;
        case 'arrowdown': anNeighbour(0, 1); break;
        case 's': $('ta-smart').checked = !$('ta-smart').checked; SS('smart', $('ta-smart').checked);
            anFlash(`Smart annotate ${$('ta-smart').checked ? 'on' : 'off'}`); break;
        case 'n': anDoneNext(); break;
        case 'd': if (AN.q >= 0) anMarkQuad(AN.q, !anIsDone(AN.cur, AN.q)); break;
        case 'h': $('ta-show').checked = !$('ta-show').checked; anRedraw(); break;
        case 'u': anToggleOpen(); break;
        case '+': case '=': anZoomCentre(1.5); break;
        case '-': case '_': anZoomCentre(1 / 1.5); break;
        case '[': case ']': {
            if (AN.tool === 'move') break;
            const v = AN[cur]; const step = cur === 'pen' ? 1 : v < 20 ? 2 : 5;
            AN[cur] = Math.max(cur === 'pen' ? 1 : 2, Math.min(cur === 'pen' ? 40 : 150, v + (k === ']' ? step : -step)));
            $(cur === 'pen' ? 'ta-pen' : 'ta-er').value = AN[cur]; SS(cur, AN[cur]); anSizeUI(); break;
        }
        default: return;
        }
        e.preventDefault();
    });
    on(document, 'keyup', (e) => { if (e.key === ' ' && AN.space) { AN.space = false; anToolUI(); } });

    return () => {
        if (AN.dirty) anSave(); // starts the request before the page is torn down
        AN.alive = false;
        clearTimeout(AN.saveTimer); clearTimeout(anFlash.t); clearTimeout(AN.hlTimer);
        if (AN.imgUrl) URL.revokeObjectURL(AN.imgUrl);
        cleanups.forEach((f) => f());
    };
}
